import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Person } from '../../types/api';

interface PersonModalProps {
    isOpen: boolean;
    onClose: () => void;
    person: Person | null;
    onEdit: (person: Person) => void;
    onDelete: (personId: string) => void;
}

export function PersonModal({ isOpen, onClose, person, onEdit, onDelete }: PersonModalProps) {
    if (!person) return null;

    const formatDate = (date: string | null) => {
        if (!date) return 'Chưa rõ';
        return new Date(date).toLocaleDateString('vi-VN');
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-25" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all">
                                <div className="flex items-start justify-between mb-4">
                                    <Dialog.Title className="text-2xl font-bold text-gray-900">
                                        {person.fullName}
                                    </Dialog.Title>
                                    <button
                                        onClick={onClose}
                                        className="text-gray-400 hover:text-gray-500"
                                    >
                                        <X className="w-6 h-6" />
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    {/* Basic Info */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-medium text-gray-500">Tên gọi</label>
                                            <p className="mt-1 text-gray-900">{person.givenName || 'Chưa có'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-500">Giới tính</label>
                                            <p className="mt-1 text-gray-900">
                                                {person.gender === 'MALE' ? 'Nam' : person.gender === 'FEMALE' ? 'Nữ' : 'Khác'}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Dates */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-medium text-gray-500">Ngày sinh</label>
                                            <p className="mt-1 text-gray-900">{formatDate(person.birthDate)}</p>
                                        </div>
                                        {person.deathDate && (
                                            <div>
                                                <label className="text-sm font-medium text-gray-500">Ngày mất</label>
                                                <p className="mt-1 text-gray-900">{formatDate(person.deathDate)}</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Family summary */}
                                    {((person.parents?.length ?? 0) > 0 || (person.children?.length ?? 0) > 0 || (person.spouses?.length ?? 0) > 0) && (
                                        <div className="grid grid-cols-3 gap-4 pt-2 border-t border-gray-100">
                                            {(person.parents?.length ?? 0) > 0 && (
                                                <div>
                                                    <label className="text-sm font-medium text-gray-500">Cha mẹ</label>
                                                    <div className="mt-1 space-y-1">
                                                        {(person.parents || []).map(p => (
                                                            <p key={p.id} className="text-sm text-gray-700">{p.fullName}</p>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {(person.spouses?.length ?? 0) > 0 && (
                                                <div>
                                                    <label className="text-sm font-medium text-gray-500">Vợ/Chồng</label>
                                                    <div className="mt-1 space-y-1">
                                                        {(person.spouses || []).map(s => (
                                                            <p key={s.id} className="text-sm text-gray-700">{s.fullName}</p>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {(person.children?.length ?? 0) > 0 && (
                                                <div>
                                                    <label className="text-sm font-medium text-gray-500">Con ({person.children?.length ?? 0})</label>
                                                    <div className="mt-1 space-y-1">
                                                        {(person.children || []).slice(0, 3).map(c => (
                                                            <p key={c.id} className="text-sm text-gray-700">{c.fullName}</p>
                                                        ))}
                                                        {(person.children?.length ?? 0) > 3 && (
                                                            <p className="text-xs text-gray-400">+{(person.children?.length ?? 0) - 3} người khác</p>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Notes */}
                                    {person.notes && (
                                        <div>
                                            <label className="text-sm font-medium text-gray-500">Ghi chú</label>
                                            <p className="mt-1 text-gray-700 whitespace-pre-wrap">{person.notes}</p>
                                        </div>
                                    )}

                                    {/* Photo */}
                                    {person.photoUrl && (
                                        <div>
                                            <label className="text-sm font-medium text-gray-500">Ảnh</label>
                                            <img
                                                src={person.photoUrl}
                                                alt={person.fullName}
                                                className="mt-2 rounded-lg max-h-64 object-cover"
                                            />
                                        </div>
                                    )}

                                    {/* Actions */}
                                    <div className="flex gap-3 pt-4 border-t">
                                        <Link
                                            to={`/person/${person.id}`}
                                            onClick={onClose}
                                            className="btn bg-gray-100 text-gray-700 hover:bg-gray-200 flex-1 flex items-center justify-center gap-2"
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                            Xem chi tiết
                                        </Link>
                                        <button
                                            onClick={() => onEdit(person)}
                                            className="btn btn-primary flex-1"
                                        >
                                            Chỉnh sửa
                                        </button>
                                        <button
                                            onClick={() => onDelete(person.id)}
                                            className="btn bg-red-600 text-white hover:bg-red-700 flex-1"
                                        >
                                            Xóa
                                        </button>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}
